@if (view(); as v) {
  @if (draft(); as d) {
    <app-path-filter-panel
      class="h-full w-full"
      [title]="'form.compatibility.panel.title' | translate: { target: v.targetLabel }"
      [entityType]="d.target.toUpperCase()"
      [count]="null"
      [error]="null"
      (closed)="session.end()">
      <div panel-body class="flex flex-col gap-4">
        <section class="flex flex-col gap-1.5">
          <span class="form-label">{{ 'form.compatibility.panel.type' | translate }}</span>
          <div class="flex flex-wrap items-center gap-2">
            <div
              class="gx-segmented"
              role="radiogroup"
              [attr.aria-label]="'form.compatibility.panel.type' | translate">
              @for (option of typeOptions; track option.soft; let index = $index) {
                <button
                  #typeRadio
                  type="button"
                  role="radio"
                  class="seg"
                  [class.is-active]="d.soft === option.soft"
                  [attr.aria-checked]="d.soft === option.soft"
                  [tabIndex]="d.soft === option.soft ? 0 : -1"
                  (click)="update({ soft: option.soft })"
                  (keydown)="onTypeKeydown($event, index)">
                  {{ option.label | translate }}
                </button>
              }
            </div>
            @if (d.soft) {
              <label class="text-text-secondary flex items-center gap-1.5 text-xs">
                {{ 'form.compatibility.panel.penalty' | translate }}
                <input
                  type="number"
                  min="0"
                  step="any"
                  class="form-input w-28 font-mono"
                  [class.has-error]="problem() === 'penalty'"
                  [value]="d.penalty ?? ''"
                  (input)="setPenalty($event)" />
              </label>
            }
          </div>
        </section>

        <section class="flex flex-col gap-1.5">
          <span class="form-label">
            {{ 'form.compatibility.panel.filter' | translate: { target: v.targetLabel } }}
          </span>
          <app-path-filter-composer
            [draft]="d.filter"
            (draftChange)="update({ filter: $event })"
            [vocabulary]="v.vocabulary"
            mode="compat"
            [selfLabel]="v.selfLabel"
            [showPhrase]="false" />
        </section>

        @if (v.takesConditions || d.kept.length > 0) {
          <section
            class="border-weak flex flex-col gap-3 border-t pt-3"
            data-testid="compat-when">
            <div class="flex flex-col gap-0.5">
              <span class="form-label">{{
                'form.compatibility.panel.when' | translate
              }}</span>
              <p class="form-help">
                {{ 'form.compatibility.panel.when_help' | translate }}
              </p>
            </div>

            @if (v.context; as context) {
              <div class="flex flex-col gap-1.5">
                <span class="text-text-secondary text-xs font-medium">
                  {{ 'form.compatibility.panel.context' | translate }}
                </span>
                @if (context.enforceable) {
                  <app-path-filter-composer
                    [draft]="d.context"
                    (draftChange)="update({ context: $event })"
                    [vocabulary]="v.contextVocabulary"
                    mode="compat"
                    [showPhrase]="false" />
                } @else {
                  <p class="form-help">
                    {{ 'form.compatibility.panel.not_enforceable' | translate }}
                  </p>
                }
              </div>
            }

            @if (v.position; as position) {
              <div class="flex flex-col gap-1.5">
                <span class="text-text-secondary text-xs font-medium">
                  {{ 'form.compatibility.panel.position' | translate }}
                </span>
                <div class="flex items-center gap-4">
                  @for (option of positionOptions(); track option.position) {
                    <app-checkbox
                      [label]="
                        'form.compatibility.panel.position_' + option.position | translate
                      "
                      [checked]="d.positions.includes(option.position)"
                      [disabled]="!position.enforceable"
                      [onChange]="option.toggle" />
                  }
                </div>
                @if (!position.enforceable) {
                  <p class="form-help">
                    {{ 'form.compatibility.panel.not_enforceable' | translate }}
                  </p>
                }
              </div>
            }

            @if (v.serviceWindow; as serviceWindow) {
              <div class="flex flex-col gap-1.5" data-testid="compat-service-window">
                <span class="text-text-secondary text-xs font-medium">
                  {{ 'form.compatibility.panel.service_window' | translate }}
                </span>
                <div class="flex items-center gap-2">
                  <input
                    type="time"
                    step="1"
                    class="form-input w-32 font-mono"
                    [attr.aria-label]="'form.compatibility.panel.window_start' | translate"
                    [disabled]="!serviceWindow.enforceable"
                    [value]="shownWindow()?.start ?? ''"
                    (input)="setWindow('start', $event)" />
                  <span class="text-text-tertiary font-mono text-xs">..</span>
                  <input
                    type="time"
                    step="1"
                    class="form-input w-32 font-mono"
                    [attr.aria-label]="'form.compatibility.panel.window_end' | translate"
                    [disabled]="!serviceWindow.enforceable"
                    [value]="shownWindow()?.end ?? ''"
                    (input)="setWindow('end', $event)" />
                </div>
                @if (!serviceWindow.enforceable) {
                  <p class="form-help">
                    {{ 'form.compatibility.panel.not_enforceable' | translate }}
                  </p>
                }
              </div>
            }

            @if (d.kept.length > 0) {
              <div class="flex flex-col gap-1.5" data-testid="compat-kept">
                <span class="text-text-secondary text-xs font-medium">
                  {{ 'form.compatibility.panel.kept' | translate }}
                </span>
                <p class="form-help">
                  {{ 'form.compatibility.panel.kept_help' | translate }}
                </p>
                @for (text of keptTexts(); track $index; let i = $index) {
                  <div
                    class="border-weak bg-surface flex items-center gap-2 rounded-md border px-2 py-1">
                    <app-dsl-text
                      class="min-w-0 flex-1 truncate text-xs"
                      [text]="text"
                      [title]="text" />
                    <button
                      type="button"
                      class="btn btn-icon hover:text-danger"
                      [attr.aria-label]="
                        'form.compatibility.panel.remove_condition' | translate
                      "
                      (click)="removeKept(i)">
                      <i [class]="ICONS.trash" aria-hidden="true"></i>
                    </button>
                  </div>
                }
              </div>
            }
          </section>
        }
      </div>

      <app-path-filter-phrase
        panel-phrase
        label="form.compatibility.phrase.label"
        [segments]="phrase()" />

      <div panel-footer class="flex w-full items-center gap-2">
        @if (problem(); as problem) {
          <span class="text-text-tertiary text-xs">
            {{ 'form.compatibility.panel.problem.' + problem | translate }}
          </span>
        }
        <span class="flex-1"></span>
        <button type="button" class="btn btn-cancel" (click)="session.end()">
          {{ 'common.cancel' | translate }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          data-testid="compat-apply"
          [disabled]="problem() !== null"
          (click)="apply()">
          {{ 'form.compatibility.panel.apply' | translate }}
        </button>
      </div>
    </app-path-filter-panel>
  }
}
